import type { SerializedError } from '@reduxjs/toolkit';
import { type FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query';
import useCurrentUser from 'hooks/useCurrentUser';
import type React from 'react';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import { useGetUserBusinessUnitsQuery } from 'services/api/businessUnits.api';
import type { BusinessUnit } from 'services/types/businessUnit';

interface BusinessUnitsContextType {
  businessUnits: BusinessUnit[] | undefined;
  isLoading: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
  // Utility functions
  getBusinessUnitById: (id: number) => BusinessUnit | undefined;
  getBusinessUnitsByCompany: (companyName: string) => BusinessUnit[];
  companyNames: string[];
}

const BusinessUnitsContext = createContext<BusinessUnitsContextType | undefined>(undefined);

export const BusinessUnitsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const currentUser = useCurrentUser();
  const previousAccessTokenRef = useRef<string | null>(currentUser?.accessToken ?? null);

  const {
    data: businessUnits,
    isLoading,
    error,
    refetch,
  } = useGetUserBusinessUnitsQuery(currentUser?.accessToken ? null : skipToken);

  // Refetch business units when user gets a new access token (after login or token refresh)
  useEffect(() => {
    const currentToken = currentUser?.accessToken;
    const previousToken = previousAccessTokenRef.current;

    if (currentToken && currentToken !== previousToken) {
      refetch().catch(() => {});
    }

    previousAccessTokenRef.current = currentToken || null;
  }, [currentUser?.accessToken, refetch]);

  const value = useMemo(() => {
    // Utility functions
    const getBusinessUnitById = (id: number) => businessUnits?.find((bu) => bu.id === id);

    const getBusinessUnitsByCompany = (companyName: string) =>
      businessUnits?.filter((bu) => bu.company?.name === companyName) ?? [];

    const companyNames = Array.from(new Set(businessUnits?.map((bu) => bu.company?.name).filter(Boolean) ?? []));

    return {
      businessUnits,
      isLoading,
      error,
      getBusinessUnitById,
      getBusinessUnitsByCompany,
      companyNames,
    };
  }, [businessUnits, isLoading, error]);

  return <BusinessUnitsContext.Provider value={value}>{children}</BusinessUnitsContext.Provider>;
};

export function useBusinessUnits() {
  const context = useContext(BusinessUnitsContext);
  if (context === undefined) {
    throw new Error('useBusinessUnits must be used within a BusinessUnitsProvider');
  }
  return context;
}
